
import { useState } from 'react';

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const RegistrationModal = ({ isOpen, onClose }: RegistrationModalProps) => {
  const [formData, setFormData] = useState({
    companyName: '',
    businessNumber: '',
    email: '',
    phone: '',
    representativeName: '',
    category: '',
    region: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://readdy.ai/api/form/d67jm8tbd17lru29kgc0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          companyName: formData.companyName,
          businessNumber: formData.businessNumber,
          email: formData.email,
          phone: formData.phone,
          representativeName: formData.representativeName,
          category: formData.category,
          region: formData.region,
          message: formData.message
        }).toString()
      });

      if (response.ok) {
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsSubmitted(false);
    setFormData({
      companyName: '',
      businessNumber: '',
      email: '',
      phone: '',
      representativeName: '',
      category: '',
      region: '',
      message: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-xl font-bold text-gray-900">제조업체 입점 신청</h2>
            <p className="text-sm text-gray-500 mt-1">전국 선장님, 선주님과 연결되세요</p>
          </div>
          <button 
            onClick={handleClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl text-gray-500"></i>
          </button>
        </div>

        {isSubmitted ? (
          /* Success State */
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-4xl text-teal-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">신청이 완료되었습니다!</h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              입점 신청서가 성공적으로 접수되었습니다.<br />
              담당자가 검토 후 빠른 시일 내에 연락드리겠습니다.
            </p>
            <button
              onClick={handleClose}
              className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 rounded-xl font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              확인
            </button>
          </div>
        ) : (
          /* Form */
          <form 
            id="manufacturer-registration-form"
            data-readdy-form
            onSubmit={handleSubmit} 
            className="p-6 space-y-5"
          >
            {/* Company Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                회사명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                required
                placeholder="예: (주)해양물산"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Business Number */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                사업자등록번호 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="businessNumber"
                value={formData.businessNumber}
                onChange={handleChange}
                required
                placeholder="예: 123-45-67890"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Representative Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                대표자 또는 담당자 성함 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="representativeName"
                value={formData.representativeName}
                onChange={handleChange}
                required
                placeholder="예: 홍길동"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                이메일 <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="예: contact@company.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                연락처 <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="예: 010-1234-5678"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                주요 취급 품목
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
              >
                <option value="">선택해주세요</option>
                <option value="어구류 전문">어구류 전문</option>
                <option value="갑판용품 전문">갑판용품 전문</option>
                <option value="기관물품 전문">기관물품 전문</option>
                <option value="종합">종합</option>
              </select>
            </div>

            {/* Region */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                소재 지역
              </label>
              <select
                name="region"
                value={formData.region}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
              >
                <option value="">선택해주세요</option>
                <option value="부산">부산</option>
                <option value="인천">인천</option>
                <option value="울산">울산</option>
                <option value="경남">경남</option>
                <option value="경북">경북</option>
                <option value="전남">전남</option>
                <option value="전북">전북</option>
                <option value="충남">충남</option>
                <option value="강원">강원</option>
                <option value="제주">제주</option>
                <option value="기타">기타</option>
              </select>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                추가 메시지
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                maxLength={500}
                rows={3}
                placeholder="회사 소개나 주력 제품에 대해 간단히 알려주세요"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
              ></textarea>
              <p className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white py-4 rounded-xl font-bold transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-xl"></i>
                  <span>제출 중...</span>
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill text-xl"></i>
                  <span>입점 신청하기</span>
                </>
              )}
            </button>

            <p className="text-xs text-gray-500 text-center">
              제출하신 정보는 입점 심사 목적으로만 사용됩니다.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default RegistrationModal;
